import React, { useState, useEffect } from "react";
import { withRouter } from "react-router-dom";
import { connect } from "react-redux";
import { compose } from "redux";
import _ from "lodash";
import { CampaignType, CampaignCategory } from "pages/dealScreener/Email/constants";
import { ROUTE_DEAL_SCREENER_EDC, ROUTE_EDC_MASTER_CONTACT_LIST } from "constants/routes";

import { Layout } from "../components";
import {
  createEmailCampaign,
  getCampaignTypes,
  getEmailCampaigns,
  deleteEmailCampaign
} from "../emailCampaign.store";

import { EdcDashboardBody, EdcDashboardHeader, DeleteCampaignDialog, NewBlastModal } from "./components";
import { DASHBOARD_VIEW_OPTIONS, DASHBOARD_VIEW_OPTION_TYPES, SAMPLE_METRICS } from "./constants";

const Header = ({
  maxWidth,
  setModalOpened,
  selectedDashboardView,
  setSelectedDashboardView,
  searchText,
  setSearchText,
  campaignStatusOptions,
  handleContactListClicked,
  emailMetrics
}) => (
  <EdcDashboardHeader
    maxWidth={maxWidth}
    setModalOpened={setModalOpened}
    selectedDashboardView={selectedDashboardView}
    setSelectedDashboardView={setSelectedDashboardView}
    searchText={searchText}
    setSearchText={setSearchText}
    campaignStatusOptions={campaignStatusOptions}
    handleContactListClicked={handleContactListClicked}
    emailMetrics={emailMetrics}
  />
);

const Content = ({
  maxWidth,
  campaigns,
  selectedDashboardView,
  setDeleteCampaignDialogOpened,
  setSelectedCampaignIdForDeletion,
  history
}) => (
  <EdcDashboardBody
    maxWidth={maxWidth}
    campaigns={campaigns}
    selectedDashboardView={selectedDashboardView}
    setDeleteCampaignDialogOpened={setDeleteCampaignDialogOpened}
    setSelectedCampaignIdForDeletion={setSelectedCampaignIdForDeletion}
    history={history}
  />
);

const EmailCampaignDashboard = ({
  createEmailCampaign,
  getEmailCampaigns,
  getCampaignTypes,
  deleteEmailCampaign,
  history,
  campaign,
  campaigns
}) => {
  const MAX_WIDTH = "1920px";
  const [newCampaignFlag, setNewCampaignFlag] = useState(false);
  const [campaignData, setCampaignData] = useState([]);
  const [viewableCampaigns, setViewableCampaigns] = useState([]);
  const [createCampaignModalOpened, setCreateCampaignModalOpened] = useState(false);
  const [deleteCampaignDialogOpened, setDeleteCampaignDialogOpened] = useState(false);
  const [selectedDashboardView, setSelectedDashboardView] = useState(DASHBOARD_VIEW_OPTION_TYPES.IN_PROGRESS.value);
  const [selectedCampaignIdForDeletion, setSelectedCampaignIdForDeletion] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [emailMetrics, setEmailMetrics] = useState(SAMPLE_METRICS); // Need to set the email metrics from Postmark.

  useEffect(() => {
    getCampaignTypes();
    getEmailCampaigns({ campaignStatusId: "" });
  }, []);

  useEffect(() => {
    setCampaignData(campaigns);
  }, [campaigns]);

  useEffect(() => {
    const campaignsFilteredBySearchText = sortCampaignsByDate(campaignData.filter(campaign => campaign.campaignStatusId === selectedDashboardView && campaign.name.toLowerCase().includes(searchText.toLowerCase())));
    const campaignsFilteredWithNoText = campaignData.filter(data => data.campaignStatusId === selectedDashboardView);
    searchText ? setViewableCampaigns(campaignsFilteredBySearchText) : setViewableCampaigns(campaignsFilteredWithNoText);
  }, [searchText]);

  useEffect(() => {
    const campaignsFilteredByStatusAndDate = sortCampaignsByDate(campaignData.filter(data => data.campaignStatusId === selectedDashboardView));
    setViewableCampaigns(campaignsFilteredByStatusAndDate);
  }, [campaignData, selectedDashboardView]);

  useEffect(() => {
    if (!_.isEmpty(campaign?.data) && Boolean(newCampaignFlag)) {
      const { id: campaignId = "", campaignTypeId = "" } = campaign?.data;
      const campaignCategory = Object.values(CampaignType)?.find(ct => ct.id === campaignTypeId)?.categoryId;
      campaignCategory === CampaignCategory.DEAL.id ?
        history.push(ROUTE_DEAL_SCREENER_EDC(campaignId), { from: history.location.pathname }) :
        history.push(`/campaigns/${campaignId}`, { from: history.location.pathname });
    }

    return () => {
      setNewCampaignFlag(false);
    };
  }, [campaign]);

  const sortCampaignsByDate = (_campaigns = []) => (
    (selectedDashboardView === DASHBOARD_VIEW_OPTION_TYPES.IN_PROGRESS.value) ?
      _campaigns?.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)) :
      _campaigns?.sort((a, b) => new Date(b.scheduledDateTime) - new Date(a.scheduledDateTime))
  );

  const closeCreateCampaignModal = () => setCreateCampaignModalOpened(false);
  const closeDeleteCampaignDialog = () => {
    setDeleteCampaignDialogOpened(false);
    setSelectedCampaignIdForDeletion(null);
  };

  const handleCreateCampaignClicked = (name, campaignTypeId) => createEmailCampaign({ name, campaignTypeId });
  const handleContactListClicked = () => history.push(ROUTE_EDC_MASTER_CONTACT_LIST, { from: history.location.pathname });
  const handleDeleteCampaign = campaignId => {
    deleteEmailCampaign({ campaignId });
    setSelectedCampaignIdForDeletion(null);
    closeDeleteCampaignDialog();
  };

  return (
    <Layout
      header={
        <Header
          maxWidth={MAX_WIDTH}
          setModalOpened={setCreateCampaignModalOpened}
          selectedDashboardView={selectedDashboardView}
          setSelectedDashboardView={setSelectedDashboardView}
          searchText={searchText}
          setSearchText={setSearchText}
          campaignStatusOptions={DASHBOARD_VIEW_OPTIONS}
          handleContactListClicked={handleContactListClicked}
          emailMetrics={emailMetrics}
        />
      }
      content={
        <Content
          maxWidth={MAX_WIDTH}
          campaigns={viewableCampaigns}
          selectedDashboardView={selectedDashboardView}
          setDeleteCampaignDialogOpened={setDeleteCampaignDialogOpened}
          setSelectedCampaignIdForDeletion={setSelectedCampaignIdForDeletion}
          history={history}
        />
      }
    >
      {
        createCampaignModalOpened &&
          <NewBlastModal
            open={createCampaignModalOpened}
            onClose={closeCreateCampaignModal}
            onCreateClicked={handleCreateCampaignClicked}
            setNewCampaignFlag={setNewCampaignFlag}
          />
      }
      <DeleteCampaignDialog
        open={deleteCampaignDialogOpened}
        handleNo={closeDeleteCampaignDialog}
        handleYes={() => {
          // Optimistic UI Update
          const filteredCampaigns = sortCampaignsByDate(viewableCampaigns.filter(campaign => campaign.id !== selectedCampaignIdForDeletion));
          setCampaignData(campaignData.filter(campaign => campaign.id !== selectedCampaignIdForDeletion));
          setViewableCampaigns(filteredCampaigns);
          handleDeleteCampaign(selectedCampaignIdForDeletion);
        }}
      />
    </Layout>
  );
};

const mapStateToProps = ({ emailCampaign }) => ({ ...emailCampaign });

export default compose(
  withRouter,
  connect(
    mapStateToProps,
    {
      createEmailCampaign,
      getCampaignTypes,
      getEmailCampaigns,
      deleteEmailCampaign
    }
  )
)(EmailCampaignDashboard);
